import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import type {
  RecipeChangeDiffItem,
  RecipeChangeRequest,
} from "~/types/recipeChange";
import type { RecipeFormData } from "~/types/production";

// recipe_change_requests is not in the generated Supabase types yet, so the table
// and RPCs are reached through an untyped client handle (same approach the codebase
// uses for other post-generation tables). Results are cast to the domain type.
const db = supabase as unknown as {
  from: (table: string) => any;
  rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: any; error: any }>;
};

const QUERY_KEY = "recipe-change-requests";

interface RequestInput {
  recipeId: string;
  payload: RecipeFormData;
  changes: RecipeChangeDiffItem[];
  reason: string;
}

// Queue of proposed recipe changes (pending / approved / rejected). Proposers
// (produccion-admin, logistica) submit; approvers (produccion-admin, admin,
// superadmin) resolve — a change never touches the live recipe until approved.
export const useRecipeChangeRequests = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: requests = [], isLoading } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await db
        .from("recipe_change_requests")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as RecipeChangeRequest[];
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: ["recipes"] });
  };

  const request = useMutation({
    mutationFn: async ({ recipeId, payload, changes, reason }: RequestInput) => {
      const { error } = await db.rpc("request_recipe_change", {
        p_recipe_id: recipeId,
        p_payload: payload,
        p_changes: changes,
        p_reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({
        title: "Solicitud enviada",
        description: "El cambio quedó pendiente de autorización.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo enviar la solicitud.",
        variant: "destructive",
      });
    },
  });

  const approve = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await db.rpc("approve_recipe_change", {
        p_request_id: requestId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({
        title: "Cambio autorizado",
        description: "La receta fue actualizada con el cambio aprobado.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo autorizar el cambio.",
        variant: "destructive",
      });
    },
  });

  const reject = useMutation({
    mutationFn: async ({
      requestId,
      reason,
    }: {
      requestId: string;
      reason: string;
    }) => {
      const { error } = await db.rpc("reject_recipe_change", {
        p_request_id: requestId,
        p_reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({
        title: "Solicitud rechazada",
        description: "La solicitud fue marcada como rechazada.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo rechazar la solicitud.",
        variant: "destructive",
      });
    },
  });

  return {
    requests,
    isLoading,
    request: request.mutateAsync,
    approve: approve.mutateAsync,
    reject: reject.mutateAsync,
    isRequesting: request.isPending,
    isApproving: approve.isPending,
    isRejecting: reject.isPending,
    pendingCount: requests.filter((r) => r.status === "pending").length,
  };
};
